import React, { useState, useRef, useEffect, useCallback } from 'react';
import Cropper from 'cropperjs';
import 'cropperjs/dist/cropper.css';

interface HeadshotEditorProps {
    avatarUrl: string | null;
    croppedAvatarUrl: string | null;
    placeholderUrl: string;
    saveUrl: string;
    removeUrl: string;
    csrfToken: string;
    cropData?: {
        x: number;
        y: number;
        width: number;
        height: number;
    };
}

const HeadshotEditor: React.FC<HeadshotEditorProps> = ({
    avatarUrl,
    croppedAvatarUrl,
    placeholderUrl,
    saveUrl,
    removeUrl,
    csrfToken,
    cropData
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(croppedAvatarUrl);
    const [isSaving, setIsSaving] = useState(false);
    const [currentImageUrl, setCurrentImageUrl] = useState<string | null>(avatarUrl);

    const imageRef = useRef<HTMLImageElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const cropperRef = useRef<Cropper | null>(null);
    const newImageUrlRef = useRef<string | null>(null);

    const initCropper = useCallback(() => {
        if (imageRef.current && !cropperRef.current && currentImageUrl) {
            const hasCropData = cropData && cropData.width > 0 && cropData.height > 0;

            cropperRef.current = new Cropper(imageRef.current, {
                aspectRatio: 4 / 5,
                viewMode: 1,
                autoCropArea: hasCropData ? 0.5 : 1, // Use smaller area if we have crop data to restore
                dragMode: 'move',
                restore: false,
                guides: true,
                center: false,
                highlight: false,
                cropBoxMovable: true,
                cropBoxResizable: true,
                toggleDragModeOnDblclick: false,
                ready() {
                    // Restore saved crop position if available
                    if (hasCropData) {
                        // Use longer delay to ensure cropper is fully ready
                        setTimeout(() => {
                            if (cropperRef.current) {
                                cropperRef.current.setData({
                                    x: Number(cropData.x),
                                    y: Number(cropData.y),
                                    width: Number(cropData.width),
                                    height: Number(cropData.height)
                                });
                            }
                        }, 100);
                    }
                }
            });
        }
    }, [currentImageUrl, cropData]);

    useEffect(() => {
        if (isModalOpen && imageRef.current && currentImageUrl) {
            // Small delay to ensure DOM is ready
            const timer = setTimeout(() => {
                initCropper();
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [isModalOpen, currentImageUrl, initCropper]);

    useEffect(() => {
        const handleProfileImageUpdate = (event: CustomEvent) => {
            if (event.detail && event.detail.url) {
                console.log("HeadshotEditor received update event:", event.detail.url);
                setPreviewUrl(event.detail.url);
                setCurrentImageUrl(event.detail.url);
            }
        };

        window.addEventListener('profile-image-updated' as any, handleProfileImageUpdate);
        return () => {
            window.removeEventListener('profile-image-updated' as any, handleProfileImageUpdate);
        };
    }, []);

    const openModal = () => {
        setIsModalOpen(true);
    };

    const closeModal = () => {
        if (isSaving) return;

        // Destroy cropper
        if (cropperRef.current) {
            cropperRef.current.destroy();
            cropperRef.current = null;
        }

        // Revoke temporary URL
        if (newImageUrlRef.current) {
            URL.revokeObjectURL(newImageUrlRef.current);
            newImageUrlRef.current = null;
        }

        // Reset to original image
        setCurrentImageUrl(avatarUrl);
        setIsModalOpen(false);
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            // Revoke previous URL
            if (newImageUrlRef.current) {
                URL.revokeObjectURL(newImageUrlRef.current);
            }

            const newUrl = URL.createObjectURL(file);
            newImageUrlRef.current = newUrl;
            setCurrentImageUrl(newUrl);

            // If cropper exists, replace image. Otherwise, useEffect will init it.
            if (cropperRef.current) {
                cropperRef.current.replace(newUrl);
            }
        }
    };

    const handleContinue = async () => {
        if (!cropperRef.current || isSaving) return;

        setIsSaving(true);

        try {
            // Get crop data first to validate
            const data = cropperRef.current.getData();

            // Validate crop data
            if (!data || isNaN(data.x) || isNaN(data.y) || isNaN(data.width) || isNaN(data.height)) {
                throw new Error('Invalid crop data');
            }

            // Get cropped canvas
            const canvas = cropperRef.current.getCroppedCanvas({
                width: 400,
                height: 500
            });

            if (!canvas) {
                throw new Error('Failed to get cropped canvas');
            }

            // Create FormData
            const formData = new FormData();

            // Add crop data
            formData.append('user[avatar_crop_x]', String(Math.round(data.x)));
            formData.append('user[avatar_crop_y]', String(Math.round(data.y)));
            formData.append('user[avatar_crop_width]', String(Math.round(data.width)));
            formData.append('user[avatar_crop_height]', String(Math.round(data.height)));
            formData.append('continue_only', 'true');

            // If new file was selected, add it
            if (fileInputRef.current?.files?.[0]) {
                formData.append('user[avatar]', fileInputRef.current.files[0]);
            }

            // Submit via AJAX
            const response = await fetch(saveUrl, {
                method: 'PATCH',
                body: formData,
                headers: {
                    'X-CSRF-Token': csrfToken
                }
            });

            // Check response
            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                const serverMessage = data.message || `Request failed with status ${response.status}`;
                throw new Error(serverMessage);
            }

            // Save successful - close modal and update images
            const newUrl = canvas.toDataURL('image/jpeg');
            console.log("Setting preview URL:", newUrl.substring(0, 50) + "...");
            setPreviewUrl(newUrl);

            // Force update DOM elements immediately
            const imagesToUpdate = [
                document.getElementById('navbar-profile-image'),
                document.getElementById('headshot-preview-main')
            ];

            // Add all generic avatar images
            document.querySelectorAll('img[alt="User avatar"]').forEach(img => imagesToUpdate.push(img as unknown as HTMLElement));
            document.querySelectorAll('.profile-header-image-container img').forEach(img => imagesToUpdate.push(img as unknown as HTMLElement));

            imagesToUpdate.forEach(img => {
                if (img instanceof HTMLImageElement) {
                    img.src = newUrl;
                }
            });

            // Dispatch event for React components to update
            const event = new CustomEvent('profile-image-updated', { detail: { url: newUrl } });
            window.dispatchEvent(event);

            if (cropperRef.current) {
                cropperRef.current.destroy();
                cropperRef.current = null;
            }
            setIsModalOpen(false);

        } catch (error: any) {
            console.error('Error saving image:', error);
            const errorMessage = error.message || 'Error saving image. Please try again.';
            alert(errorMessage);
            setIsSaving(false);
        }
    };

    const hasImage = previewUrl || croppedAvatarUrl;

    return (
        <div className="bg-white sm:max-h-96 max-w-xs">
            {/* Preview Section with Hover Edit */}
            {hasImage ? (
                <div className="relative group cursor-pointer" onClick={openModal}>
                    <img
                        id="headshot-preview-main"
                        src={previewUrl || croppedAvatarUrl || ''}
                        alt="User avatar"
                        className="w-full rounded-lg object-cover"
                    />
                    {/* Hover Overlay */}
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all rounded-lg">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            <span className="text-white text-sm mt-1 font-medium">Edit</span>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="relative group cursor-pointer" onClick={openModal}>
                    <img
                        id="headshot-preview-main"
                        src={placeholderUrl}
                        alt="Add headshot"
                        className="w-full rounded-xl object-contain"
                        style={{ backgroundColor: 'white' }}
                    />
                    {/* Hover Overlay */}
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all rounded-xl">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                            </svg>
                            <span className="text-white text-sm mt-1 font-medium">Add Photo</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal */}
            {isModalOpen && (
                <div
                    className="fixed z-50 inset-0 overflow-y-auto"
                    aria-labelledby="modal-title"
                    role="dialog"
                    aria-modal="true"
                >
                    <div className="flex items-center justify-center min-h-screen py-4 px-4 text-center sm:block sm:p-0">
                        {/* Backdrop */}
                        <div
                            className="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity"
                            aria-hidden="true"
                            onClick={closeModal}
                        />

                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                        {/* Modal Content */}
                        <div className="inline-block align-bottom bg-white rounded-lg text-left shadow-xl transform transition-all sm:mb-4 sm:mt-20 sm:align-middle w-full sm:max-w-sm overflow-hidden">
                            <div className="scrollable-content overflow-y-auto px-4 pt-5 pb-4 sm:p-6" style={{ maxHeight: '80vh' }}>
                                {/* Close Button */}
                                <div className="absolute top-0 right-0 pt-4 pr-4 z-10">
                                    <button
                                        type="button"
                                        className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                        onClick={closeModal}
                                        disabled={isSaving}
                                    >
                                        <span className="sr-only">Close</span>
                                        <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>

                                <div className="flex items-start flex-col">
                                    <div className="mt-3 sm:mt-0 text-left w-full">
                                        <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                                            {hasImage ? 'Edit Profile Picture' : 'Add Profile Picture'}
                                        </h3>

                                        <ol className="my-4 text-sm leading-5 text-gray-700">
                                            <li>1. Upload your profile photo below.</li>
                                            <li>2. Move the crop box to frame your photo.</li>
                                            <li>3. Click "Continue" to save your changes.</li>
                                        </ol>

                                        {/* Crop Container */}
                                        <div className="flex flex-row mt-4 md:mt-8 mb-4 w-full text-center justify-center items-center">
                                            {currentImageUrl ? (
                                                <img
                                                    ref={imageRef}
                                                    src={currentImageUrl}
                                                    alt="Crop preview"
                                                    crossOrigin="anonymous"
                                                    style={{ maxWidth: '100%' }}
                                                />
                                            ) : (
                                                <div className="w-full h-64 bg-gray-100 flex items-center justify-center rounded-lg">
                                                    <span className="text-gray-400">No image selected</span>
                                                </div>
                                            )}
                                        </div>

                                        {/* File Input */}
                                        <div className="mt-4">
                                            <label className="block text-sm font-medium text-gray-700">
                                                Upload New Profile Picture
                                            </label>
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                accept="image/jpg,image/jpeg,image/png"
                                                onChange={handleFileChange}
                                                className="mt-1 p-2 shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 rounded-md"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Buttons */}
                                <div className="flex flex-row p-4 items-center justify-center gap-3">
                                    <button
                                        type="button"
                                        className="mt-4 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                        onClick={closeModal}
                                        disabled={isSaving}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-500 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                        onClick={handleContinue}
                                        disabled={isSaving || !currentImageUrl}
                                    >
                                        {isSaving ? 'Saving...' : 'Continue'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HeadshotEditor;
